import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { Group } from '../../primitives/Group.js';
import { Text } from '../../primitives/Text.js';
import styles from './ImageAttachmentPicker.module.css';

export type ImageAttachmentView = {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  signedUrl: string;
};

type Props = {
  attachments: ImageAttachmentView[];
  compact?: boolean;
  /** Thumbnails inside a message composer (above text). */
  embedded?: boolean;
  onRemove?: (index: number) => void;
};

export function ImageAttachmentGrid({
  attachments,
  compact = false,
  embedded = false,
  onRemove,
}: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const close = useCallback(() => setActiveIndex(null), []);
  const active = activeIndex != null ? attachments[activeIndex] : null;

  const step = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        if (current == null || attachments.length === 0) return current;
        return (current + delta + attachments.length) % attachments.length;
      });
    },
    [attachments.length],
  );

  useEffect(() => {
    if (active == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [active, close, step]);

  const removeAt = useCallback(
    (index: number) => {
      onRemove?.(index);
      setActiveIndex((current) => {
        if (current == null) return current;
        if (current === index) return null;
        if (current > index) return current - 1;
        return current;
      });
    },
    [onRemove],
  );

  if (!attachments.length) return null;

  return (
    <>
      <Group
        gap="xs"
        wrap="wrap"
        className={embedded ? styles.embedded : compact ? styles.compact : undefined}
      >
        {attachments.map((a, index) => (
          <div
            key={a.id}
            className={styles.thumbWrap}
            style={embedded ? undefined : { maxWidth: compact ? 128 : 140 }}
          >
            <button
              type="button"
              className={styles.thumbButton}
              onClick={() => setActiveIndex(index)}
              aria-label={`Agrandir ${a.fileName}`}
            >
              <img src={a.signedUrl} alt={a.fileName} className={styles.thumbImg} />
              {!compact && !embedded ? (
                <Text size="xs" c="secondary" className={styles.thumbName}>
                  {a.fileName}
                </Text>
              ) : null}
            </button>
            {onRemove ? (
              <button
                type="button"
                className={styles.thumbRemove}
                onClick={() => removeAt(index)}
                aria-label={`Retirer ${a.fileName}`}
              >
                <IconX size={14} stroke={2.2} aria-hidden />
              </button>
            ) : null}
          </div>
        ))}
      </Group>

      {active && typeof document !== 'undefined'
        ? createPortal(
            <div
              className={styles.lightboxBackdrop}
              role="dialog"
              aria-modal="true"
              aria-label={active.fileName}
              onClick={close}
            >
              <button
                type="button"
                className={styles.lightboxClose}
                onClick={close}
                aria-label="Fermer"
              >
                <IconX size={20} stroke={2} aria-hidden />
              </button>
              <figure className={styles.lightboxFigure} onClick={(e) => e.stopPropagation()}>
                <img src={active.signedUrl} alt={active.fileName} className={styles.lightboxImg} />
                <figcaption className={styles.lightboxCaption}>{active.fileName}</figcaption>
              </figure>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
